import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { mutate } from 'swr'
import { putFetcher } from 'api/swr'
import { toast } from '@/components/ui/use-toast'
import { pickerGridCards } from 'Editor/lib/visibleComponents'
import Cpt from './Cpt'
import { useBatchSelectStore } from './batchSelectStore'
import { CptGrid } from './ui'

function SortableCpt({ cpt, cpts, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cpt.id, disabled })

  return (
    <Cpt
      cpt={cpt}
      cpts={cpts}
      itemRef={setNodeRef}
      itemStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      isDragging={isDragging}
      dragHandleProps={
        disabled
          ? undefined
          : {
              ...attributes,
              ...listeners,
            }
      }
    />
  )
}

export const SortableCptGrid = ({ cpts, stackCpts, uploading }) => {
  const { tid } = useParams()
  const batchEnabled = useBatchSelectStore((s) => s.enabled)
  const cards = useMemo(() => pickerGridCards(cpts), [cpts])
  const [localIds, setLocalIds] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setLocalIds(null)
  }, [cpts])

  const ids = localIds ?? cards.map((cpt) => cpt.id)
  const ordered = useMemo(() => {
    const byId = new Map(cards.map((cpt) => [cpt.id, cpt]))
    return ids.map((id) => byId.get(id)).filter(Boolean)
  }, [cards, ids])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const onDragEnd = useCallback(
    async (event) => {
      const { active, over } = event
      if (saving || !over || active.id === over.id) return
      const oldIndex = ids.findIndex((id) => id === active.id)
      const newIndex = ids.findIndex((id) => id === over.id)
      if (oldIndex < 0 || newIndex < 0) return

      const next = arrayMove(ids, oldIndex, newIndex)
      setLocalIds(next)
      setSaving(true)
      try {
        await putFetcher('/components/reorder', { arg: { ids: next } })
        await mutate(
          ['/components', { tid }],
          (current) => {
            if (!Array.isArray(current)) return current
            const indexById = new Map(next.map((id, index) => [id, index]))
            return current.map((cpt) =>
              indexById.has(cpt.id)
                ? { ...cpt, playIndex: indexById.get(cpt.id) }
                : cpt,
            )
          },
          { revalidate: false },
        )
        setLocalIds(null)
      } catch {
        toast({
          variant: 'destructive',
          title: 'Order update failed',
          description: 'Could not save the new component order. Please try again.',
        })
        setLocalIds(null)
      } finally {
        setSaving(false)
      }
    },
    [ids, saving, tid],
  )

  const dragDisabled = batchEnabled || saving

  return (
    <div className="relative">
      {saving ? (
        <div className="mb-1 text-xs text-muted-foreground">正在保存顺序…</div>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={dragDisabled ? undefined : onDragEnd}
      >
        <CptGrid className={saving ? 'pointer-events-none opacity-70' : undefined}>
          {uploading}
          <SortableContext
            items={ids}
            strategy={rectSortingStrategy}
            disabled={dragDisabled}
          >
            {ordered.map((cpt) => (
              <SortableCpt
                key={cpt.id}
                cpt={cpt}
                cpts={stackCpts ?? cpts}
                disabled={dragDisabled}
              />
            ))}
          </SortableContext>
        </CptGrid>
      </DndContext>
    </div>
  )
}
